import React, { useState, useEffect, useRef } from 'react';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import { Row, Col, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { FaSave, FaUser, FaCalendar, FaClock, FaBuilding, FaCity, FaTimes } from 'react-icons/fa';
import { Autocomplete } from '@react-google-maps/api';
import { useModal } from './ModalContext'; 
import { useProfiles } from './ProfileContext'; 
import axios from 'axios';
import swal from 'sweetalert'; 

const AddProfile = () => {
    const { isAddProfileOpen, toggleAddProfile, profileToEdit, setProfileToEdit } = useModal();
    const { fetchProfiles } = useProfiles(); 
    const cityAutocompleteRef = useRef(null); 
    const [loading, setLoading] = useState(false); // Loading state for the spinner
    const [formData, setFormData] = useState({
        id: null,
        name: '',
        dob: '',
        time_of_birth: '',
        birth_city: '',
        birth_state: '',
        birth_country: '',
        latitude: '',
        longitude: '',
        timezone: '',
        gender: '',
    });

    useEffect(() => {
        if (profileToEdit) {
            setFormData({
                id: profileToEdit.id || null,
                name: profileToEdit.name || '',
                dob: profileToEdit.dob || '',
                time_of_birth: profileToEdit.time_of_birth || '',
                birth_city: profileToEdit.birth_city || '',
                birth_state: profileToEdit.birth_state || '',
                birth_country: profileToEdit.birth_country || '',
                latitude: profileToEdit.latitude || '',
                longitude: profileToEdit.longitude || '',
                timezone: profileToEdit.timezone || '',
                gender: profileToEdit.gender || '',
            });
        } else {
            setFormData({
                id: null,
                name: '',
                dob: '',
                time_of_birth: '',
                birth_city: '',
                birth_state: '',
                birth_country: '',
                latitude: '',
                longitude: '',
                timezone: '',
                gender: '',
            });
        }
    }, [profileToEdit]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handlePlaceChange = async () => {
        if (cityAutocompleteRef.current && cityAutocompleteRef.current.getPlace()) {
            const place = cityAutocompleteRef.current.getPlace();
            const birth_city = place.address_components
                .filter(component => component.types.includes('locality'))
                .map(component => component.long_name)
                .join(', ');

            const birth_state = place.address_components
                .filter(component => component.types.includes('administrative_area_level_1'))
                .map(component => component.long_name)
                .join(', ');

            const birth_country = place.address_components
                .filter(component => component.types.includes('country'))
                .map(component => component.long_name)
                .join(', ');

            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            const timestamp = Math.floor(Date.now() / 1000);
            const timezoneResponse = await axios.get(
                `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
            );
            const timezone = timezoneResponse.data.rawOffset / 3600;

            setFormData({
                ...formData,
                birth_city,
                birth_state,
                birth_country,
                latitude: lat,
                longitude: lng,
                timezone,
            });
        }
    };

    const validateForm = () => {
        if (!formData.name) {
            swal('Validation Error', 'Name is required', 'error');
            return false;
        }
        if (!formData.dob) {
            swal('Validation Error', 'Date of Birth is required', 'error');
            return false;
        }
        if (!formData.time_of_birth) {
            swal('Validation Error', 'Time of Birth is required', 'error');
            return false;
        }
        if (!formData.birth_city) {
            swal('Validation Error', 'Birth City is required', 'error');
            return false;
        }
        if (!formData.gender) {
            swal('Validation Error', 'Gender is required', 'error');
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (validateForm()) {
            setLoading(true); // Set loading state to true
            const token = localStorage.getItem('token');
            try {
                const endpoint = formData.id ? `edit-profile/${formData.id}` : 'save-profile';
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/${endpoint}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (response && response.status === 201) {
                    swal('Success', response.data.message || 'Profile saved successfully', 'success');
                    toggleAddProfile(); 
                    setProfileToEdit(null); 
                    await fetchProfiles(); 
                } else {
                    swal('Error', 'Unexpected error. Please try again.', 'error');
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 422) {
                        const errorMessages = Object.values(error.response.data.errors).flat().join('\n');
                        swal('Validation Error', errorMessages, 'error');
                    } else if (error.response.status === 500) {
                        swal('Error', error.response.data.message || 'Failed to save profile. Please try again.', 'error');
                    } else {
                        swal('Error', 'Unexpected error. Please try again.', 'error');
                    }
                } else if (error.request) {
                    swal('Error', 'Network error. Please check your connection.', 'error');
                } else {
                    swal('Error', 'An unexpected error occurred. Please try again.', 'error');
                }
            }
            setLoading(false); // Set loading state to false
        }
    };

    return (
        <div className={`wallet-overlay ${isAddProfileOpen ? 'show' : ''}`}>
            {loading && (
                <div className="loader-overlay show">
                    <div className="load-inner">
                        <div className="spinner-border" role="status"></div>
                        <span className="loader-text">Creating a comprehensive astrological profile, including all charts. This may take a few moments, please be patient...</span>
                    </div>
                </div>
            )}
            <div className="wallet-modal">
                <div className="wallet-header">
                    <IconButton className="arrow-left" onClick={() => { 
                        toggleAddProfile(); 
                        setProfileToEdit(null); 
                    }} aria-label="Back">
                        <NavigateBeforeOutlinedIcon />
                    </IconButton>
                    <h5 className="wallet-title">{profileToEdit ? 'Edit Profile' : 'Add New Profile'}</h5>
                </div>
                <div className="wallet-body">
                    <div className="terms-content">
                        <Form className="form-wrap">
                            <FormGroup>
                                <Label for="name"><FaUser /> Name</Label>
                                <Input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} />
                            </FormGroup>
                            <FormGroup>
                                <Label for="birth_city"><FaBuilding /> Birth City</Label>
                                <Autocomplete
                                    onLoad={ref => cityAutocompleteRef.current = ref}
                                    onPlaceChanged={handlePlaceChange}
                                >
                                    <Input type="text" name="birth_city" id="birth_city" value={formData.birth_city} onChange={handleInputChange} />
                                </Autocomplete>
                            </FormGroup>
                            <FormGroup>
                                <Label for="dob"><FaCalendar /> Date of Birth</Label>
                                <Input type="date" name="dob" id="dob" value={formData.dob} onChange={handleInputChange} />
                            </FormGroup>
                            <FormGroup>
                                <Label for="time_of_birth"><FaClock /> Time of Birth</Label>
                                <Input type="time" name="time_of_birth" id="time_of_birth" value={formData.time_of_birth} onChange={handleInputChange} />
                            </FormGroup>

                            <FormGroup className="hideclass">
                                <Label for="birth_state"><FaBuilding /> Birth State</Label>
                                <Input type="text" name="birth_state" id="birth_state" value={formData.birth_state} readOnly />
                            </FormGroup>
                            <FormGroup className="hideclass">
                                <Label for="birth_country"><FaCity /> Birth Country</Label>
                                <Input type="text" name="birth_country" id="birth_country" value={formData.birth_country} readOnly />
                            </FormGroup>
                            <FormGroup>
                                <Label for="gender"><FaUser /> Gender</Label>
                                <Input type="select" name="gender" id="gender" value={formData.gender} onChange={handleInputChange}>
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </Input>
                            </FormGroup>

                            <Row className="mt-3">
                                <Col xs="6" className="text-center">
                                    <Button color="" className="green-back-btn" onClick={handleSave} disabled={loading}>
                                        <FaSave className="mr-2" /> Save
                                    </Button>
                                </Col>
                                <Col xs="6" className="text-center">
                                    <Button color="" className="green-outlined-btn" onClick={() => { 
                                        toggleAddProfile(); 
                                        setProfileToEdit(null); 
                                    }} disabled={loading}>
                                        <FaTimes className="mr-2" /> Cancel
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddProfile;
